import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Receipt, Plus, Loader2, Trash2, Pencil, Calendar, Download,
  Scale, Wallet, HandCoins, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatINR } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Venue', 'Catering', 'Photography', 'Decor', 'Attire', 'Jewellery',
  'Makeup', 'Music & DJ', 'Invitations', 'Travel', 'Accommodation',
  'Gifts', 'Priest & Rituals', 'Miscellaneous',
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

const emptyForm = {
  description: '',
  category: '',
  expense_date: '',
  total_amount: '',
  amount_paid: '',
  payment_status: 'full',
  paid_by: 'groom',
  split_5050: true,
  vendor_id: '',
  payment_method: '',
  notes: '',
};

const NONE = '__none__';

export const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filterPayer, setFilterPayer] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eRes, vRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/api/expenses`, { withCredentials: true }),
        axios.get(`${API_URL}/api/vendors`, { withCredentials: true }),
        axios.get(`${API_URL}/api/expenses/summary`, { withCredentials: true }),
      ]);
      setExpenses(eRes.data);
      setVendors(vRes.data);
      setSummary(sRes.data);
    } catch (error) {
      toast.error('Error loading expenses');
    } finally {
      setLoading(false);
    }
  };

  const brideName = summary?.bride_name || 'Bride';
  const groomName = summary?.groom_name || 'Groom';
  const nameFor = (side) => (side === 'bride' ? brideName : groomName);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (expense) => {
    setEditingId(expense.id);
    setForm({
      description: expense.description || '',
      category: expense.category || '',
      expense_date: expense.expense_date || '',
      total_amount: String(expense.total_amount ?? ''),
      amount_paid: String(expense.amount_paid ?? ''),
      payment_status: expense.is_full_payment ? 'full' : 'part',
      paid_by: expense.paid_by || 'groom',
      split_5050: expense.split_5050 !== false,
      vendor_id: expense.vendor_id || '',
      payment_method: expense.payment_method || '',
      notes: expense.notes || '',
    });
    setDialogOpen(true);
  };

  const totalAmountNum = parseFloat(form.total_amount) || 0;
  const paidAmountNum =
    form.payment_status === 'full' ? totalAmountNum : parseFloat(form.amount_paid) || 0;
  const remainingPreview = Math.max(totalAmountNum - paidAmountNum, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (totalAmountNum <= 0) {
      toast.error('Enter a total amount greater than zero');
      return;
    }
    if (form.payment_status === 'part' && paidAmountNum > totalAmountNum) {
      toast.error('Amount paid cannot be more than the total amount');
      return;
    }

    const payload = {
      description: form.description.trim(),
      category: form.category || null,
      expense_date: form.expense_date || null,
      total_amount: totalAmountNum,
      amount_paid: paidAmountNum,
      paid_by: form.paid_by,
      split_5050: form.split_5050,
      vendor_id: form.vendor_id || null,
      payment_method: form.payment_method || null,
      notes: form.notes.trim() || null,
    };

    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API_URL}/api/expenses/${editingId}`, payload, { withCredentials: true });
        toast.success('Expense updated');
      } else {
        await axios.post(`${API_URL}/api/expenses`, payload, { withCredentials: true });
        toast.success('Expense added');
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save expense');
    } finally {
      setSaving(false);
    }
  };

  const deleteExpense = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/expenses/${id}`, { withCredentials: true });
      toast.success('Expense removed');
      fetchData();
    } catch (error) {
      toast.error('Error deleting expense');
    }
  };

  const filtered = useMemo(() => {
    return expenses.filter((e) => {
      if (filterPayer !== 'all' && e.paid_by !== filterPayer) return false;
      if (filterStatus === 'full' && !e.is_full_payment) return false;
      if (filterStatus === 'part' && e.is_full_payment) return false;
      return true;
    });
  }, [expenses, filterPayer, filterStatus]);

  const exportCsv = () => {
    const rows = [
      ['Date', 'Description', 'Category', 'Vendor', 'Total', 'Paid', 'Remaining', 'Paid By', 'Split 50-50', 'Payment', 'Method', 'Notes'],
      ...filtered.map((e) => [
        e.expense_date || '',
        e.description,
        e.category || '',
        e.vendor_name || '',
        e.total_amount,
        e.amount_paid,
        e.remaining_amount,
        nameFor(e.paid_by),
        e.split_5050 ? 'Yes' : 'No',
        e.is_full_payment ? 'Full' : 'Part',
        e.payment_method || '',
        e.notes || '',
      ]),
    ];
    const csv = rows
      .map((r) => r.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `vivah-expenses-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-serif font-bold tracking-tight">Expenses</h1>
          <p className="text-muted-foreground mt-1">
            Everything spent so far, who paid, and what is still owed
          </p>
        </div>
        <div className="flex items-center gap-2">
          {expenses.length > 0 && (
            <Button variant="outline" className="rounded-full" onClick={exportCsv} data-testid="export-expenses-button">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          )}
          <Button className="rounded-full" onClick={openAdd} data-testid="add-expense-button">
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Spent</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="expenses-total-paid">
              {formatINR(summary?.total_paid || 0, { compact: true })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              of {formatINR(summary?.total_expense || 0, { compact: true })} committed
            </p>
          </CardContent>
        </Card>

        <Card className={`shadow-sm ${summary?.total_remaining > 0 ? 'border-secondary/40 bg-secondary/5' : ''}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Still To Pay</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="expenses-total-remaining">
              {formatINR(summary?.total_remaining || 0, { compact: true })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              across {summary?.part_payment_count || 0} part payment{summary?.part_payment_count === 1 ? '' : 's'}
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider truncate">
              Paid by {groomName}
            </CardTitle>
            <HandCoins className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="expenses-paid-groom">
              {formatINR(summary?.paid_by_groom || 0, { compact: true })}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider truncate">
              Paid by {brideName}
            </CardTitle>
            <HandCoins className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="expenses-paid-bride">
              {formatINR(summary?.paid_by_bride || 0, { compact: true })}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-sm border-primary/20 bg-primary/5" data-testid="settlement-card">
        <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Scale className="h-5 w-5 text-primary" />
            </div>
            <div>
              {summary?.settlement_amount > 0 ? (
                <>
                  <p className="font-medium">
                    {nameFor(summary.settlement_from)} owes {nameFor(summary.settlement_to)}{' '}
                    <span className="text-primary font-serif text-lg">
                      {formatINR(summary.settlement_amount, { compact: true })}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    To square up the 50-50 shared expenses
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    All settled on shared expenses
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Both sides have covered an equal share
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            Shared pool {formatINR(summary?.shared_total || 0, { compact: true })} · fair share each{' '}
            {formatINR(summary?.fair_share_each || 0, { compact: true })}
          </div>
        </CardContent>
      </Card>

      {expenses.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <Select value={filterPayer} onValueChange={setFilterPayer}>
            <SelectTrigger className="w-[190px]" data-testid="filter-payer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone</SelectItem>
              <SelectItem value="groom">Paid by {groomName}</SelectItem>
              <SelectItem value="bride">Paid by {brideName}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[190px]" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All payments</SelectItem>
              <SelectItem value="full">Fully paid</SelectItem>
              <SelectItem value="part">Part paid</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">
            {filtered.length} of {expenses.length} expenses
          </span>
        </div>
      )}

      {expenses.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Receipt className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              No expenses logged yet. Add everything you have spent so far.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((expense) => (
            <Card key={expense.id} className="shadow-sm" data-testid="expense-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-medium">{expense.description}</h3>
                      {expense.category && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                          {expense.category}
                        </span>
                      )}
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border ${
                          expense.is_full_payment
                            ? 'bg-green-500/10 text-green-700 border-green-500/20'
                            : 'bg-secondary/15 text-secondary-foreground border-secondary/30'
                        }`}
                      >
                        {expense.is_full_payment ? 'Full payment' : 'Part payment'}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border ${
                          expense.split_5050
                            ? 'bg-primary/10 text-primary border-primary/20'
                            : 'bg-muted text-muted-foreground border-border'
                        }`}
                      >
                        {expense.split_5050 ? 'Split 50-50' : 'Not split'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mt-1">
                      {expense.expense_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(expense.expense_date).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric',
                          })}
                        </span>
                      )}
                      <span>
                        Paid by <span className="font-medium text-foreground">{nameFor(expense.paid_by)}</span>
                      </span>
                      {expense.vendor_name && <span>Vendor: {expense.vendor_name}</span>}
                      {expense.payment_method && (
                        <span className="capitalize">{expense.payment_method.replace('_', ' ')}</span>
                      )}
                    </div>
                    {expense.notes && (
                      <p className="text-sm text-muted-foreground mt-2">{expense.notes}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-xl font-serif font-bold text-primary">
                        {formatINR(expense.amount_paid, { compact: true })}
                      </div>
                      {!expense.is_full_payment && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {formatINR(expense.remaining_amount, { compact: true })} remaining of{' '}
                          {formatINR(expense.total_amount, { compact: true })}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(expense)}
                        data-testid="edit-expense-button"
                        className="text-muted-foreground hover:text-primary"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteExpense(expense.id)}
                        data-testid="delete-expense-button"
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => !saving && setDialogOpen(open)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
            <DialogDescription>Record what was spent, by whom, and what is still pending</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>What was it for</Label>
              <Input
                placeholder="e.g., Venue advance, Sangeet outfit"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                required
                data-testid="expense-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date (optional)</Label>
                <Input
                  type="date"
                  value={form.expense_date}
                  onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                  data-testid="expense-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Category (optional)</Label>
                <Select
                  value={form.category || NONE}
                  onValueChange={(val) => setForm({ ...form, category: val === NONE ? '' : val })}
                >
                  <SelectTrigger data-testid="expense-category-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Paid by</Label>
              <Select value={form.paid_by} onValueChange={(val) => setForm({ ...form, paid_by: val })}>
                <SelectTrigger data-testid="expense-paidby-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="groom">{groomName}</SelectItem>
                  <SelectItem value="bride">{brideName}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Total amount (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.total_amount}
                  onChange={(e) => setForm({ ...form, total_amount: e.target.value })}
                  required
                  data-testid="expense-total-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Payment</Label>
                <Select
                  value={form.payment_status}
                  onValueChange={(val) => setForm({ ...form, payment_status: val })}
                >
                  <SelectTrigger data-testid="expense-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full payment</SelectItem>
                    <SelectItem value="part">Part payment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.payment_status === 'part' && (
              <div className="space-y-2">
                <Label>Amount paid so far (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.amount_paid}
                  onChange={(e) => setForm({ ...form, amount_paid: e.target.value })}
                  required
                  data-testid="expense-paid-input"
                />
                <p className="text-sm text-muted-foreground">
                  Remaining:{' '}
                  <span className="font-medium text-foreground">{formatINR(remainingPreview)}</span>
                </p>
              </div>
            )}

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <Label className="cursor-pointer">Split 50-50</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {form.split_5050
                    ? 'Shared equally — counts towards settlement'
                    : `Borne fully by ${nameFor(form.paid_by)}`}
                </p>
              </div>
              <Switch
                checked={form.split_5050}
                onCheckedChange={(checked) => setForm({ ...form, split_5050: checked })}
                data-testid="expense-split-switch"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vendor (optional)</Label>
                <Select
                  value={form.vendor_id || NONE}
                  onValueChange={(val) => setForm({ ...form, vendor_id: val === NONE ? '' : val })}
                >
                  <SelectTrigger data-testid="expense-vendor-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {vendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Method (optional)</Label>
                <Select
                  value={form.payment_method || NONE}
                  onValueChange={(val) => setForm({ ...form, payment_method: val === NONE ? '' : val })}
                >
                  <SelectTrigger data-testid="expense-method-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Anything worth remembering..."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="expense-notes-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full rounded-full"
              disabled={saving}
              data-testid="submit-expense-button"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? 'Save Changes' : 'Add Expense'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
